import { z } from "zod";

/** Per-route settings, stored in `activities.transfer`. */
export const transferRouteConfigSchema = z.object({
  /** `return` asks for a return time; `hourly` is the driver-by-the-hour routes. */
  trip: z.enum(["one_way", "return", "hourly"]),
  /** Which second place the customer names, if any. */
  secondPlace: z.enum(["none", "restaurant", "club", "camp"]),
});
export type TransferRouteConfig = z.infer<typeof transferRouteConfigSchema>;

const transferPlaceBase = z.object({
  name: z.string().trim().min(1).max(200),
  address: z.string().trim().max(300).optional(),
  placeId: z.string().max(300).optional(),
  /** Set when the customer picked one of our own listed venues. */
  activityId: z.string().uuid().optional(),
  /** Required unless `typed` is true. */
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  /**
   * The guest typed an address Google does not list, so there is no pin. The
   * team confirms the spot (and whether the outside-Marrakech fee applies) on
   * WhatsApp. Owner decision 2026-09-23.
   */
  typed: z.boolean().optional(),
});

/** Pinned places need coordinates; a typed one needs a real address (3 to 200 characters). */
function checkTransferPlace(p: z.infer<typeof transferPlaceBase>, ctx: z.RefinementCtx) {
  if (p.typed === true) {
    if (p.name.length < 3) {
      ctx.addIssue({ code: "custom", path: ["name"], message: "A typed address needs at least 3 characters" });
    }
    return;
  }
  if (p.lat === undefined) ctx.addIssue({ code: "custom", path: ["lat"], message: "Required" });
  if (p.lng === undefined) ctx.addIssue({ code: "custom", path: ["lng"], message: "Required" });
}

export const transferPlaceSchema = transferPlaceBase.superRefine(checkTransferPlace);
export type TransferPlace = z.infer<typeof transferPlaceSchema>;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** What the booking form sends in `POST /bookings/checkout` as `transfer`. */
export const transferBookingSchema = z.object({
  pickup: transferPlaceSchema,
  place: transferPlaceSchema.optional(),
  returnTime: hhmm.optional(),
  /**
   * The guest does not know the return time yet and will send it on WhatsApp.
   * Stands in for `returnTime` on a return route; when both are sent the time
   * wins and the flag is dropped.
   */
  returnTimeLater: z.boolean().optional(),
  /**
   * A ride booked together with a table: that booking's reference
   * (MA-...). Admin shows the two as one booking.
   */
  withBookingId: z.string().trim().min(1).max(64).optional(),
});
export type TransferBookingInput = z.infer<typeof transferBookingSchema>;

/** What the API stores in `reservations.transfer`. */
export const transferRecordSchema = z.object({
  pickup: transferPlaceSchema,
  place: transferPlaceBase
    .extend({ kind: z.enum(["restaurant", "club", "camp"]) })
    .superRefine(checkTransferPlace)
    .optional(),
  pickupTime: hhmm.nullable(),
  returnTime: hhmm.optional(),
  /** The return time is still to be confirmed with the guest. */
  returnTimeLater: z.boolean().optional(),
  outsideZone: z.boolean(),
  outsideFee: z.number().int().nonnegative(),
  /**
   * A customer place was typed rather than picked, so it has no pin: the team
   * checks the location and the outside-Marrakech fee on WhatsApp.
   */
  addressToCheck: z.boolean().optional(),
  /**
   * A ride booked together with a table: that booking's reference
   * (MA-...). Admin shows the two as one booking.
   */
  withBookingId: z.string().trim().min(1).max(64).optional(),
});
export type TransferRecord = z.infer<typeof transferRecordSchema>;
